import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import client from "../../api/client";

const TYPES = ["Company", "Foundation", "Individual", "Not sure yet"];

const TYPE_STYLES = {
  Company: "bg-blue-50 text-blue-700",
  Foundation: "bg-purple-50 text-purple-700",
  Individual: "bg-green-50 text-green-700",
};

function Detail({ label, value, href }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      {href ? (
        <a href={href} className="text-sm text-[#294a93] hover:underline break-all">{value}</a>
      ) : (
        <p className="text-sm text-gray-800 break-words">{value}</p>
      )}
    </div>
  );
}

function EnquiryRow({ item, onDelete }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const name = `${item.first_name || ""} ${item.last_name || ""}`.trim() || item.email;
  // Enquiries from before the form revamp kept their text in sponsorship_details.
  const goals = item.goals || item.sponsorship_details;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <button type="button" onClick={() => setOpen(!open)} className="w-full text-left">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-800">{name}</span>
            {item.organization && <span className="text-xs text-gray-500">{item.organization}</span>}
            {item.sponsorship_option && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${TYPE_STYLES[item.sponsorship_option] || "bg-gray-100 text-gray-600"}`}>
                {item.sponsorship_option}
              </span>
            )}
            {item.seats && (
              <span className="text-xs bg-orange-50 text-[#d15424] px-2 py-0.5 rounded-full">{item.seats} seats</span>
            )}
          </div>
          <span className="text-xs text-gray-300">{new Date(item.created_at).toLocaleString()}</span>
        </div>
        {!open && goals && <p className="text-sm text-gray-500 mt-2 line-clamp-1">{goals}</p>}
      </button>

      {open && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Detail label="Email" value={item.email} href={`mailto:${item.email}`} />
            <Detail label="Phone" value={item.phone} href={item.phone ? `tel:${item.phone}` : undefined} />
            <Detail label="Organisation" value={item.organization} />
            <Detail label="Role" value={item.role} />
          </div>
          {goals && (
            <div className="mt-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                What they want the sponsorship to achieve
              </p>
              <p className="text-sm text-gray-700 mt-1 leading-relaxed whitespace-pre-line">{goals}</p>
            </div>
          )}
          <div className="mt-4 flex justify-end gap-2">
            {confirming ? (
              <>
                <button type="button" onClick={() => setConfirming(false)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium hover:bg-gray-50">
                  Cancel
                </button>
                <button type="button" onClick={() => onDelete(item.id)} className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700">
                  Yes, delete
                </button>
              </>
            ) : (
              <button type="button" onClick={() => setConfirming(true)} className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50">
                Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminSponsorsPage() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [page, setPage] = useState(1);

  const load = useCallback((p = 1) => {
    setLoading(true);
    client
      .get("/admin/sponsor-enquiries", { params: { search, type, page: p } })
      .then((res) => {
        setItems(res.data.data || []);
        setMeta(res.data);
        setPage(p);
      })
      .catch(() => toast.error("Failed to load sponsor enquiries."))
      .finally(() => setLoading(false));
  }, [search, type]);

  useEffect(() => { load(1); }, [load]);

  const handleDelete = (id) => {
    client
      .delete(`/admin/sponsor-enquiries/${id}`)
      .then(() => {
        toast.success("Enquiry deleted.");
        load(page);
      })
      .catch(() => toast.error("Couldn't delete that enquiry."));
  };

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Sponsor Enquiries</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Submissions from the Become a Sponsor form. Click an enquiry to see the full details.
          {meta?.total != null && <span className="text-gray-400"> · {meta.total} total</span>}
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-5">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, or organisation…"
          className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 w-64"
        />
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30"
        >
          <option value="">All types</option>
          {TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      <div className="space-y-3">
        {loading ? (
          [1, 2, 3, 4].map((i) => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
            <p className="text-4xl mb-3">🤝</p>
            <p className="text-sm font-medium text-gray-600">No sponsor enquiries yet</p>
          </div>
        ) : (
          items.map((item) => <EnquiryRow key={item.id} item={item} onDelete={handleDelete} />)
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button disabled={page === 1} onClick={() => load(page - 1)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors">← Prev</button>
          <span className="px-4 py-2 text-sm text-gray-500">Page {page} of {meta.last_page}</span>
          <button disabled={page === meta.last_page} onClick={() => load(page + 1)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors">Next →</button>
        </div>
      )}
    </div>
  );
}
